import {
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Tree } from "src/atoms/Tree";
import { Checkbox } from "src/atoms/Checkbox";
import { CategoryTreeNode } from "./CategoryTree.styled";
import { Flex } from "@otrium/core";
import { Text } from "@otrium/atoms";
import classNames from "classnames";
import { useCategoryTreeContext } from "src/hooks/context/use-category-tree-context";
import { CategoryNodesType, CategoryNodeType } from "./types";
import { useRouter } from "next/router";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";
import { CATEGORIES_FACET } from "src/utils/algolia";

type CategoryNodeProps = {
  category: CategoryNodeType;
  children?: CategoryNodesType;
  isRoot?: boolean;
  showCount?: boolean;
  parentSelected?: boolean;
  parentCategoryPageId?: string | undefined;
  rootItemIds: string[];
  parentSlug?: string;
  gridRef: MutableRefObject<HTMLDivElement | null>;
};

function Node({
  category,
  parentSelected,
  parentCategoryPageId,
  rootItemIds,
  parentSlug,
  gridRef,
}: CategoryNodeProps): JSX.Element {
  const shouldTriggerRefine = useRef(false);

  const router = useRouter();
  const { tree, refine } = useCategoryTreeContext();
  const [isSelected, setSelected] = useState(() => {
    const url = new URL(location.toString());

    return url.searchParams.getAll("cat_slug").includes(category.slug);
  });

  const updatedSelectedCategories = useCallback(() => {
    const url = new URL(location.toString());
    const selected = url.searchParams
      .getAll("cat_slug")
      .includes(category.slug);

    setSelected(selected);
  }, [category.slug]);

  useEffect(() => {
    router.events.on("routeChangeComplete", updatedSelectedCategories);

    return () => {
      router.events.off("routeChangeComplete", updatedSelectedCategories);
    };
  }, [updatedSelectedCategories, router.events]);

  const isRoot = useMemo(
    () => rootItemIds.includes(category.itemId),
    [rootItemIds, category.itemId]
  );

  const children = useMemo(() => {
    const children = tree.filter(
      (subCategory) => subCategory.parentId === category.itemId
    );

    return children;
  }, [tree, category.itemId]);

  const { handleSearchParams } = useHandleSearchParams();

  const applyFilter = useCallback(() => {
    if (!shouldTriggerRefine.current) {
      return;
    }

    const removeAction =
      children.length && !rootItemIds.includes(category.itemId)
        ? "remove-all-sub"
        : children.length
        ? "remove-all"
        : "remove";

    refine({
      categoryPageId: category.categoryPageId,
      id: category.itemId,
      parentId: category.parentId,
      parentCategoryId: !parentSelected ? undefined : parentCategoryPageId,
      action: !isSelected ? removeAction : "add",
    });

    gridRef?.current?.scrollIntoView();

    sessionStorage.removeItem("ot_scroll_position");

    shouldTriggerRefine.current = false;
  }, [
    category.categoryPageId,
    category.itemId,
    category.parentId,
    parentCategoryPageId,
    children.length,
    parentSelected,
    rootItemIds,
    isSelected,
    gridRef,
    refine,
  ]);

  const onChange = useCallback(() => {
    const url = new URL(location.toString());
    const catSlugs = url.searchParams.getAll("cat_slug");
    const selected = catSlugs.includes(category.slug);
    const selectedChildren = children.flatMap((child) =>
      catSlugs.includes(child.slug) ? [child.slug] : []
    );

    handleSearchParams({
      facet: CATEGORIES_FACET,
      value: category.slug,
      values: [...selectedChildren, category.slug],
      deletePage: true,
    });

    setSelected(!selected);

    shouldTriggerRefine.current = true;
  }, [category.slug, children, handleSearchParams]);

  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (parentSelected || isRoot) {
      return;
    }

    const url = new URL(location.toString());
    const allCategories = url.searchParams.getAll("cat_slug");
    const selected = allCategories.includes(category.slug);

    const parentSlugSelected = parentSlug
      ? allCategories.includes(parentSlug)
      : false;

    if (!parentSlugSelected && selected) {
      setSelected(false);
    }
  }, [parentSelected, isRoot, category.slug, parentSlug]);

  useEffect(() => {
    const url = new URL(location.toString());
    const allCategories = url.searchParams.getAll("cat_slug");
    const selected = allCategories.includes(category.slug);

    if (!selected) {
      setSelected(false);
    }
  }, [category.slug]);

  return (
    <CategoryTreeNode>
      <Flex placeItems="center" gap="0.5rem">
        <Checkbox
          name={category.label}
          checked={isSelected}
          value={category.value}
          onChange={onChange}
          onTransitionEnd={applyFilter}
        >
          <Text as="span" fontWeight={isRoot ? 600 : 300}>
            {category.label}
          </Text>
        </Checkbox>
      </Flex>

      {children.length ? (
        <div
          className={classNames("childNode", {
            ["expanded"]:
              (isSelected && children.length > 0) ||
              (isRoot && children.length > 0),
          })}
        >
          <Tree>
            {children.map((childCategory) => (
              <CategoryNode
                key={childCategory.itemId}
                category={childCategory}
                parentCategoryPageId={category.categoryPageId}
                parentSelected={isSelected}
                rootItemIds={rootItemIds}
                parentSlug={category.slug}
                gridRef={gridRef}
              />
            ))}
          </Tree>
        </div>
      ) : null}
    </CategoryTreeNode>
  );
}

const CategoryNode = memo(Node);

export { CategoryNode };
